import { BookOpen, Heart, MessageCircle, Hand } from "lucide-react";
import { deedsData, mapCategoryToKey } from "@/data/deeds";

// Kategorien-Definition - Harmonische Farbpalette mit gleicher Sättigung
export const CATEGORIES = {
  ibadah: {
    icon: BookOpen,
    color: "#A78BFA", // violet-400
    colorMuted: "rgba(167, 139, 250, 0.15)",
    keywords: ["quran", "qurʾan", "gebet", "beten", "prayer", "pray", "namaz", "dua", "duʿa", "dhikr", "zikir", "subhanallah", "alhamdulillah", "elhamdulillah", "istighfar", "astaghfirullah", "estağfirullah", "allah", "taubah", "tevbe", "shalat", "salat", "القرآن", "صلاة", "دعاء", "ذكر"]
  },
  herz: {
    icon: Heart,
    color: "#F472B6", // pink-400
    colorMuted: "rgba(244, 114, 182, 0.15)",
    keywords: ["geduld", "patient", "sabır", "sabar", "صبر", "vergib", "forgive", "oprosti", "affet", "maaf", "سامح", "dankbar", "grateful", "zahval", "şükür", "syukur", "شكر", "demut", "humble", "skroman", "alçakgönüllü", "rendah hati", "متواضع", "hoffnung", "hope", "nada", "umut", "harap", "أمل", "absicht", "intention", "namjera", "niyet", "niat", "نية", "herz", "heart", "srce", "kalp", "hati", "قلب", "zorn", "anger", "srdžba", "öfke", "marah", "غضب", "neid", "envy", "zavist", "kıskançlık", "iri", "حسد", "denk", "think", "misli", "düşün", "pikir", "فكر", "vertrau", "trust", "pouzdaj", "tevekkül", "percaya", "توكل"]
  },
  zunge: {
    icon: MessageCircle,
    color: "#4ADE80", // green-400
    colorMuted: "rgba(74, 222, 128, 0.15)",
    keywords: ["sprich", "speak", "govori", "konuş", "bicara", "تحدث", "sag", "say", "reci", "söyle", "ucap", "قل", "wort", "word", "riječ", "söz", "kata", "كلمة", "lob", "praise", "pohval", "öv", "puji", "امدح", "salam", "selam", "سلام", "grüß", "greet", "pozdravi", "selamla", "sapa", "حيّ", "wahrheit", "truth", "istina", "doğru", "kebenaran", "صدق", "lästern", "gossip", "ogovara", "dedikodu", "ghibah", "غيبة", "freundlich", "kind", "ljubazan", "nazik", "ramah", "لطيف"]
  },
  handlung: {
    icon: Hand,
    color: "#FBBF24", // amber-400
    colorMuted: "rgba(251, 191, 36, 0.15)",
    keywords: ["hilf", "help", "pomozi", "yardım", "bantu", "ساعد", "gib", "give", "daj", "ver", "beri", "أعطِ", "sadaqah", "sadaka", "sedekah", "صدقة", "spende", "donate", "doniraj", "bağışla", "donasi", "تبرع", "sauber", "clean", "čist", "temiz", "bersih", "نظيف", "tier", "animal", "životinja", "hayvan", "hewan", "حيوان", "nachbar", "neighbor", "komšija", "komşu", "tetangga", "جار", "eltern", "parent", "roditelj", "anne baba", "orang tua", "والد", "familie", "family", "porodica", "aile", "keluarga", "عائلة", "arbeit", "work", "posao", "iş", "kerja", "عمل", "verschenk", "gift", "pokloni", "hediye", "hadiah", "هدية", "wasser", "water", "voda", "su", "air", "ماء", "kleid", "cloth", "odjeća", "kıyafet", "pakaian", "ملابس", "essen", "eat", "jedi", "ye", "makan", "أكل", "biete", "offer", "ponudi", "ikram", "tawar", "قدم"]
  }
};

// Kategorie-Labels für alle Sprachen
export const CATEGORY_LABELS = {
  ibadah: { de: 'Ibadah', en: 'Ibadah', bs: 'Ibadet', ar: 'عبادة', id: 'Ibadah', tr: 'İbadet' },
  herz: { de: 'Herz', en: 'Heart', bs: 'Srce', ar: 'القلب', id: 'Hati', tr: 'Kalp' },
  zunge: { de: 'Zunge', en: 'Speech', bs: 'Jezik', ar: 'اللسان', id: 'Lisan', tr: 'Dil' },
  handlung: { de: 'Handlung', en: 'Action', bs: 'Djelo', ar: 'العمل', id: 'Amal', tr: 'Amel' }
};

// Stats Modal Labels für alle Sprachen
export const CATEGORY_STATS_LABELS = {
  de: { title: 'Abgeschlossene Taten', date: 'Datum', deed: 'Tat', noDeeds: 'Noch keine Taten in dieser Kategorie' },
  en: { title: 'Completed Deeds', date: 'Date', deed: 'Deed', noDeeds: 'No deeds in this category yet' },
  bs: { title: 'Završena djela', date: 'Datum', deed: 'Djelo', noDeeds: 'Još nema djela u ovoj kategoriji' },
  ar: { title: 'الأعمال المكتملة', date: 'التاريخ', deed: 'العمل', noDeeds: 'لا توجد أعمال في هذه الفئة بعد' },
  id: { title: 'Amal yang Selesai', date: 'Tanggal', deed: 'Amal', noDeeds: 'Belum ada amal di kategori ini' },
  tr: { title: 'Tamamlanan Ameller', date: 'Tarih', deed: 'Amel', noDeeds: 'Bu kategoride henüz amel yok' }
};

// Feste Kategorie-Zuordnung basierend auf Tat-Index (sprachunabhängig)
// Basiert auf der deutschen Version als Master
export const DEED_CATEGORIES = [
  // 0-9
  'herz',      // 0: Lächle heute einer Person...
  'zunge',     // 1: Begrüße heute bewusst jemanden mit Salam
  'ibadah',    // 2: Sprich heute dreimal Subhanallah
  'ibadah',    // 3: Sage heute bewusst Alhamdulillah
  'herz',      // 4: Reagiere heute geduldig
  'zunge',     // 5: Unterlasse heute jegliches Lästern
  'handlung',  // 6: Tue heute etwas Gutes für deine Eltern
  'ibadah',    // 7: Lies heute mindestens einen Vers aus dem Qurʾan
  'ibadah',    // 8: Sprich heute ein Duʿa
  'zunge',     // 9: Sprich heute bewusst ein freundliches Wort
  // 10-19
  'herz',      // 10: Unterlasse heute Worte, wenn du wütend wirst
  'herz',      // 11: Überprüfe heute deine Absicht
  'handlung',  // 12: Gib heute eine kleine Sadaqah
  'zunge',     // 13: Bedanke dich heute bei einer Person
  'handlung',  // 14: Behandle heute deinen Nachbarn freundlich
  'handlung',  // 15: Sorge heute für Sauberkeit
  'herz',      // 16: Beherrsche heute bewusst deinen Zorn
  'herz',      // 17: Vergib heute jemandem innerlich
  'handlung',  // 18: Hilf heute jemandem bei einer Kleinigkeit
  'ibadah',    // 19: Sprich heute La ilaha illa Allah
  // 20-29
  'herz',      // 20: Meide heute schlechte Gedanken
  'ibadah',    // 21: Verrichte heute ein Gebet pünktlich
  'zunge',     // 22: Sprich heute ruhig und respektvoll
  'herz',      // 23: Beginne heute eine Arbeit mit guter Absicht
  'herz',      // 24: Nutze heute deine Zeit bewusst
  'herz',      // 25: Iss heute bewusst und dankbar
  'herz',      // 26: Ziehe dich heute aus einem Streit zurück
  'handlung',  // 27: Behandle heute ein Tier mit Barmherzigkeit
  'herz',      // 28: Denke heute gut über Allah
  'ibadah',    // 29: Bitte heute Allah um Vergebung
  // 30-39
  'zunge',     // 30: Sprich heute bewusst die Wahrheit
  'herz',      // 31: Meide heute Neid
  'ibadah',    // 32: Sprich heute ein kurzes persönliches Duʿa
  'herz',      // 33: Reagiere heute sanft statt hart
  'handlung',  // 34: Verbringe heute bewusste Zeit mit deiner Familie
  'herz',      // 35: Senke heute bewusst deinen Blick
  'herz',      // 36: Bewahre heute gute Hoffnung auf Allah
  'herz',      // 37: Behandle heute Wissen mit Respekt
  'herz',      // 38: Meide heute Hochmut
  'ibadah',    // 39: Bitte heute Allah um Barakah
  // 40-49
  'ibadah',    // 40: Gedenke heute Allah leise
  'herz',      // 41: Vertraue heute Allah
  'herz',      // 42: Erwarte heute Gutes von anderen
  'herz',      // 43: Handle heute bescheiden
  'herz',      // 44: Nimm dir heute einen Moment für Nachdenken
  'handlung',  // 45: Handle heute gerecht
  'zunge',     // 46: Höre heute jemandem aufmerksam zu
  'zunge',     // 47: Sprich heute freundlich und ruhig
  'herz',      // 48: Erkenne heute Allahs Gaben bewusst an
  'herz',      // 49: Erneuere heute deine gute Absicht
  // 50-59 (Updated to match actual deeds 51-60)
  'handlung',  // 50: Unterstütze heute eine Person, die Hilfe braucht
  'handlung',  // 51: Achte heute bewusst auf die Reinheit deiner Hände
  'ibadah',    // 52: Erinnere dich heute vor dem Schlafen an Allah
  'handlung',  // 53: Achte heute darauf, dass deine Kleidung sauber ist
  'herz',      // 54: Übe dich heute in Mitgefühl gegenüber anderen
  'handlung',  // 55: Gib heute einem Tier etwas zu essen oder zu trinken
  'handlung',  // 56: Spende heute einen kleinen Betrag an Bedürftige
  'herz',      // 57: Übe heute Geduld in einer Situation
  'zunge',     // 58: Sprich heute freundlich mit jemandem am Telefon
  'ibadah',    // 59: Sprich heute Bismillah vor dem Essen
  // 60-69
  'herz',      // 60: Vergib heute jemandem, der dich verletzt hat
  'handlung',  // 61: Überprüfe heute, ob deine Handlungen halal sind
  'zunge',     // 62: Höre heute bewusst jemandem zu
  'handlung',  // 63: Kleide dich heute sauber und bescheiden
  'herz',      // 64: Achte heute auf gute Haltung
  'ibadah',    // 65: Lies heute einen Qurʾan-Vers
  'ibadah',    // 66: Bete heute für den Erfolg anderer
  'zunge',     // 67: Sage heute ein freundliches Wort zu deinem Nachbarn
  'handlung',  // 68: Halte heute dein Zuhause sauber
  'zunge',     // 69: Entschuldige dich heute bei jemandem
  // 70-79
  'zunge',     // 70: Lobe heute bewusst eine andere Person
  'zunge',     // 71: Sei heute ehrlich in allen Angelegenheiten
  'handlung',  // 72: Zeige heute besondere Freundlichkeit gegenüber Kindern
  'ibadah',    // 73: Sprich heute ein Duʿa für Verstorbene
  'herz',      // 74: Denke heute gut über die Menschen
  'herz',      // 75: Versuche heute eine schlechte Angewohnheit zu reduzieren
  'handlung',  // 76: Vermeide heute Verschwendung bei Lebensmitteln
  'ibadah',    // 77: Sprich heute ein stilles Duʿa
  'herz',      // 78: Denke heute positiv über Allahs Plan
  'herz',      // 79: Lächle heute bewusst jemandem
  // 80-89
  'herz',      // 80: Übernimm heute Verantwortung ohne zu klagen
  'handlung',  // 81: Verschenke heute etwas Kleines
  'herz',      // 82: Überdenke heute deine Worte und Taten
  'handlung',  // 83: Achte heute beim Wudhu auf sparsamen Wasserverbrauch
  'ibadah',    // 84: Lies heute ein Buch, das dich näher zu Allah bringt
  'zunge',     // 85: Begrüße heute bewusst jede Person
  'herz',      // 86: Sei heute aufmerksam gegenüber Bedürfnissen anderer
  'herz',      // 87: Übe heute Barmherzigkeit
  'handlung',  // 88: Biete heute einem Gast ein Getränk an
  'ibadah',    // 89: Sprich heute während des Tages ein kurzes Dhikr
  // 90-99
  'herz',      // 90: Starte heute jede Arbeit mit guter Absicht
  'zunge',     // 91: Verabschiede dich heute freundlich
  'herz',      // 92: Vermeide heute Prahlen und Selbstlob
  'ibadah',    // 93: Bitte heute Allah um Segen
  'zunge',     // 94: Sprich heute ein aufbauendes Wort zu Bedürftigen
  'handlung',  // 95: Tue heute bewusst etwas Gutes für eine Person
  'herz',      // 96: Sei heute rücksichtsvoll und freundlich unterwegs
  'ibadah',    // 97: Sprich heute ein kurzes Dhikr wenn du wartest
  'zunge',     // 98: Lache heute sanft, ohne andere zu verspotten
  'herz',      // 99: Denke heute vor dem Schlafen an die guten Dinge
];

// Kategorie für eine Tat bestimmen (basierend auf Index aus den neuen 300 Taten)
export const getDeedCategoryByIndex = (index, language = 'de') => {
  const deeds = deedsData[language] || deedsData.de;
  if (index >= 0 && index < deeds.length) {
    return mapCategoryToKey(deeds[index].category);
  }
  // Fallback für alte Daten
  return DEED_CATEGORIES[index % DEED_CATEGORIES.length] || 'herz';
};

// Kategorie für eine Tat bestimmen (basierend auf Deed ID)
export const getDeedCategoryById = (deedId, language = 'de') => {
  // Ramadan-Deeds sind immer ibadah
  if (typeof deedId === 'string' && deedId.startsWith('ramadan-')) {
    return 'ibadah';
  }
  const deeds = deedsData[language] || deedsData.de;
  const deed = deeds.find(d => d.id === deedId);
  if (deed) {
    return mapCategoryToKey(deed.category);
  }
  return 'herz';
};

// Fallback: Kategorie basierend auf Text (nur für Kompatibilität)
export const getDeedCategory = (text) => {
  const lowerText = text.toLowerCase();
  
  for (const [category, config] of Object.entries(CATEGORIES)) {
    if (config.keywords.some(keyword => lowerText.includes(keyword))) {
      return category;
    }
  }
  return 'herz'; // Default
};
